import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "lineItemsBody", "totalCell", "totalValue",
    "feeInput", "feeCell", "workCell",
    "addLineItemModal", "newWork", "newFee",
    "approveModal", "unapproveModal", "deleteModal",
    "requestApprovalModal"
  ]

  static values = {
    projectId: Number,
    ovid: Number,
    currency: String,
    currencyFormat: String,
    delimiter: String,
    separator: String,
    canEdit: Boolean,
    canEditBase: Boolean,
    isOwner: Boolean,
    isOtherVendor: Boolean,
    approved: Boolean
  }

  connect() {
    this._pendingDeleteId = null
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._showPendingFlash()
  }

  // ── Number/currency helpers ───────────────────────────────────────

  _parseNumber(str) {
    if (!str) return 0
    let cleaned = str.trim()
    if (this.currencyFormatValue === "european_currency_format") {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      cleaned = cleaned.replace(/'/g, "")
    } else {
      cleaned = cleaned.replace(/,/g, "")
    }
    const val = parseFloat(cleaned)
    return isNaN(val) ? 0 : val
  }

  _formatNumber(num) {
    if (num === null || num === undefined || isNaN(num)) return ""
    const abs = Math.abs(num)
    if (this.currencyFormatValue === "european_currency_format") {
      return abs.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      return abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    } else {
      return abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
  }

  // ── Vendor selector ─────────────────────────────────────────────

  changeVendor(event) {
    const ovid = event.target.value
    window.location.href = `/projects/${this.projectIdValue}/finances/other_vendors?ovid=${ovid}`
  }

  // ── Inline editing ────────────────────────────────────────────────

  updateWork(event) {
    const el = event.target
    const id = el.dataset.id
    const work = el.textContent.trim()
    this._ajax("PUT", "update_project_other_vendor_fee_schedule_line_item", { id, work })
      .then(() => this._showToast("Line item updated"))
      .catch(() => this._showToast("Failed to update line item", "error"))
  }

  updateFee(event) {
    const input = event.target
    if (input.readOnly) return
    const id = input.dataset.id
    const fee = this._parseNumber(input.value)
    input.value = fee > 0 ? this._formatNumber(fee) : ""
    this._ajax("PUT", "update_project_other_vendor_fee_schedule_line_item", { id, fee })
      .then(() => this._showToast("Fee updated"))
      .catch(() => this._showToast("Failed to update fee", "error"))
    this._recalcTotals()
  }

  updateOwnerApvd(event) {
    const id = event.target.dataset.id
    const ownerApvd = event.target.checked
    this._ajax("PUT", "update_project_other_vendor_fee_schedule_line_item", { id, owner_apvd: ownerApvd })
      .then(() => this._showToast(ownerApvd ? "Line item approved" : "Approval removed"))
      .catch(() => this._showToast("Failed to update approval", "error"))

    const row = event.target.closest("tr")
    const feeInput = row.querySelector("[data-ov-fee-schedule-target='feeInput']")
    const feeCell = row.querySelector("[data-ov-fee-schedule-target='feeCell']")
    const workCell = row.querySelector("[data-ov-fee-schedule-target='workCell']")
    if (feeInput) feeInput.readOnly = ownerApvd
    if (feeCell) feeCell.classList.toggle("bg-amber-50", !ownerApvd)
    if (workCell) {
      workCell.contentEditable = !ownerApvd && this.canEditValue
      workCell.classList.toggle("bg-amber-50", !ownerApvd && this.canEditValue)
      workCell.classList.toggle("cursor-text", !ownerApvd && this.canEditValue)
    }
  }

  // ── Add line item ─────────────────────────────────────────────────

  showAddLineItemModal(event) {
    event.preventDefault()
    this.newWorkTarget.value = ""
    this.newFeeTarget.value = ""
    this.addLineItemModalTarget.classList.remove("hidden")
  }

  hideAddLineItemModal() {
    this.addLineItemModalTarget.classList.add("hidden")
  }

  async submitAddLineItem() {
    const work = this.newWorkTarget.value.trim()
    const fee = this._parseNumber(this.newFeeTarget.value)
    if (!work) return

    const resp = await this._ajax("POST", "create_project_other_vendor_fee_schedule_line_item", {
      ovid: this.ovidValue, work, fee
    })
    if (resp?.success) {
      this._insertLineItemRow(resp.id, resp.item, work, fee)
      this.hideAddLineItemModal()
      this._recalcTotals()
      this._showToast("Line item added")
    } else {
      this._showToast(resp?.errors?.join(", ") || "Failed to add line item", "error")
    }
  }

  // ── Remove line item ──────────────────────────────────────────────

  removeLineItem(event) {
    event.preventDefault()
    this._pendingDeleteId = event.currentTarget.dataset.id
    this.deleteModalTarget.classList.remove("hidden")
  }

  hideDeleteModal() { this.deleteModalTarget.classList.add("hidden") }

  async confirmDelete() {
    const resp = await this._ajax("DELETE", "delete_project_other_vendor_fee_schedule_line_item", { id: this._pendingDeleteId })
    this.hideDeleteModal()
    if (resp === undefined) {
      this._showToast("Failed to delete line item", "error")
      return
    }

    const row = this.element.querySelector(`tr[data-id="${this._pendingDeleteId}"]`)
    if (row) {
      const tbody = row.closest("tbody")
      row.remove()
      if (tbody) {
        tbody.querySelectorAll("tr[data-id]").forEach((tr, i) => {
          tr.querySelector("td:first-child").textContent = i + 1
        })
      }
    }
    this._recalcTotals()
    this._showToast("Line item deleted")
  }

  // ── Approve / Unapprove ───────────────────────────────────────────

  showApproveModal() { this.approveModalTarget.classList.remove("hidden") }
  hideApproveModal() { this.approveModalTarget.classList.add("hidden") }
  showUnapproveModal() { this.unapproveModalTarget.classList.remove("hidden") }
  hideUnapproveModal() { this.unapproveModalTarget.classList.add("hidden") }

  async confirmApprove() {
    const resp = await this._ajax("PUT", "approve_ov_fee_schedule", { sign: "yes", ovid: this.ovidValue })
    this.hideApproveModal()
    if (resp?.approved) {
      this._flashAndReload("Fee schedule approved")
    }
  }

  async confirmUnapprove() {
    const resp = await this._ajax("PUT", "approve_ov_fee_schedule", { sign: "no", ovid: this.ovidValue })
    this.hideUnapproveModal()
    if (resp && !resp.approved) {
      this._flashAndReload("Fee schedule unapproved")
    }
  }

  // ── Request approval ──────────────────────────────────────────────

  showRequestApprovalModal() { this.requestApprovalModalTarget.classList.remove("hidden") }
  hideRequestApprovalModal() { this.requestApprovalModalTarget.classList.add("hidden") }

  async sendApprovalRequest() {
    const sections = this.requestApprovalModalTarget.querySelectorAll("[data-company-section]")
    for (const section of sections) {
      if (section.classList.contains("hidden")) continue
      const checked = section.querySelectorAll("input[name='approval_email']:checked")
      if (checked.length === 0) {
        this._showToast("Please select at least one person from each company.", "alert")
        return
      }
    }

    const checkboxes = this.requestApprovalModalTarget.querySelectorAll("input[name='approval_email']:checked")
    const emails = Array.from(checkboxes).map(cb => cb.dataset.email)
    if (emails.length === 0) return

    await this._ajax("POST", "request_ov_fee_schedule_approval", { emails: JSON.stringify(emails), ovid: this.ovidValue })
    this.hideRequestApprovalModal()
    this._flashAndReload("Approval request sent")
  }

  // ── Insert line item row into DOM ────────────────────────────────

  _insertLineItemRow(id, itemNum, work, fee) {
    const tbody = this.lineItemsBodyTarget
    if (!tbody) return

    const canEdit = this.canEditValue
    const canEditBase = this.canEditBaseValue
    const isOwner = this.isOwnerValue
    const currency = this.currencyValue

    const trashSvg = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>'

    const tr = document.createElement("tr")
    tr.className = "bg-white border-b border-l border-r border-gray-200 group"
    tr.dataset.id = id

    // Item number
    const tdItem = document.createElement("td")
    tdItem.className = "px-3 py-1 border-r border-gray-200 text-gray-500"
    tdItem.textContent = itemNum
    tr.appendChild(tdItem)

    // Work description
    const tdWork = document.createElement("td")
    tdWork.className = `px-3 py-1 border-r border-gray-200${canEdit ? " bg-amber-50 cursor-text" : ""}`
    tdWork.contentEditable = canEdit
    tdWork.dataset.action = "blur->ov-fee-schedule#updateWork"
    tdWork.dataset.ovFeeScheduleTarget = "workCell"
    tdWork.dataset.id = id
    tdWork.textContent = work
    tr.appendChild(tdWork)

    // Fee
    const tdFee = document.createElement("td")
    tdFee.className = "px-0 py-0 border-r border-gray-200 bg-amber-50"
    tdFee.dataset.ovFeeScheduleTarget = "feeCell"
    const feeDiv = document.createElement("div")
    feeDiv.className = "flex items-center"
    feeDiv.innerHTML = `<span class="pl-3 text-gray-400 select-none text-[13px]">${currency}</span>` +
      `<input type="text" inputmode="decimal" class="w-full px-1 py-1 pr-3 text-right text-[13px] bg-transparent focus:outline-none"` +
      ` data-ov-fee-schedule-target="feeInput" data-action="blur->ov-fee-schedule#updateFee" data-id="${id}"` +
      ` value="${fee > 0 ? this._formatNumber(fee) : ""}">`
    tdFee.appendChild(feeDiv)
    tr.appendChild(tdFee)

    // Owner Apvd checkbox
    const tdApvd = document.createElement("td")
    tdApvd.className = `px-3 py-1${canEditBase ? " border-r border-gray-200" : ""}`
    tdApvd.innerHTML = `<div class="flex items-center justify-center gap-1">` +
      `<span class="w-3.5"></span>` +
      `<input type="checkbox" data-action="change->ov-fee-schedule#updateOwnerApvd" data-id="${id}"` +
      ` class="w-4 h-4 rounded border-gray-300 text-brand focus:ring-brand shrink-0"${isOwner ? "" : " disabled"}>` +
      `<span class="w-3.5"></span></div>`
    tr.appendChild(tdApvd)

    // Trash button
    if (canEditBase) {
      const tdTrash = document.createElement("td")
      tdTrash.className = "px-0 py-0 text-center"
      tdTrash.innerHTML = `<button type="button" class="p-1 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"` +
        ` data-action="click->ov-fee-schedule#removeLineItem" data-id="${id}" title="Delete">${trashSvg}</button>`
      tr.appendChild(tdTrash)
    }

    // Insert before the totals row (last tr in tbody)
    const totalsRow = tbody.querySelector("tr:last-child")
    tbody.insertBefore(tr, totalsRow)
  }

  // ── Recalculate totals ────────────────────────────────────────────

  _recalcTotals() {
    let total = 0
    this.feeInputTargets.forEach(input => {
      total += this._parseNumber(input.value)
    })
    if (this.hasTotalValueTarget) {
      this.totalValueTarget.textContent = this._formatNumber(total)
    }
  }

  // ── Toast helpers ───────────────────────────────────────────────

  _flashAndReload(message, type = "success") {
    sessionStorage.setItem("ov_fee_schedule_flash", JSON.stringify({ message, type }))
    window.location.reload()
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("ov_fee_schedule_flash")
    if (!raw) return
    sessionStorage.removeItem("ov_fee_schedule_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._showToast(message, type)
    } catch { /* ignore */ }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── AJAX helper ───────────────────────────────────────────────────

  async _ajax(method, action, params = {}) {
    const url = `/projects/${this.projectIdValue}/finances/${action}`
    const body = new FormData()
    Object.entries(params).forEach(([k, v]) => body.append(k, v))

    const options = {
      method: method === "GET" ? "GET" : "POST",
      headers: { "X-CSRF-Token": this._csrfToken },
    }

    if (method !== "GET") {
      if (method !== "POST") body.append("_method", method.toLowerCase())
      options.body = body
    }

    try {
      const resp = await fetch(url, options)
      if (resp.headers.get("content-type")?.includes("application/json")) {
        return await resp.json()
      }
      return resp.ok
    } catch (e) {
      console.error("OV fee schedule request failed:", e)
    }
  }
}
